import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';
import { formatTime, formatCurrency, getStatusBadgeClass } from '../../utils/formatters';
import { ArrowLeft, MapPin, Clock, DollarSign, Calendar, FileText, MessageSquare, Navigation, Info } from 'lucide-react';
import { format } from 'date-fns';

// Parse date string as local date (not UTC) and format
function formatDateLocal(dateStr, formatStr = 'EEEE, MMMM d, yyyy') {
  if (!dateStr) return '';
  const datePart = typeof dateStr === 'string' ? dateStr.substring(0, 10) : dateStr.toISOString().substring(0, 10);
  const [year, month, day] = datePart.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return format(date, formatStr);
}

export default function ContractorGigDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [gig, setGig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);

  useEffect(() => {
    api.get(`/contractors/${user.id}/gigs`)
      .then(gigs => {
        const found = gigs.find(g => g.id === id);
        if (found) {
          setGig(found);
          setNotes(found.contractorNotes || '');
        }
      })
      .finally(() => setLoading(false));
  }, [id, user.id]);

  const handleAccept = async () => {
    await api.post(`/assignments/${gig.assignmentId}/accept`);
    setGig({ ...gig, status: 'ACCEPTED' });
  };

  const handleDecline = async () => {
    if (!confirm('Are you sure you want to decline this gig?')) return;
    await api.post(`/assignments/${gig.assignmentId}/decline`);
    navigate('/my/dashboard');
  };

  const handleSaveNotes = async () => {
    if (savingNotes) return;
    setSavingNotes(true);
    setNotesSaved(false);
    
    try {
      await api.put(`/assignments/${gig.assignmentId}/notes`, { notes });
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 3000);
    } catch (err) {
      alert('Failed to save notes');
    }
    setSavingNotes(false);
  };

  const getDirectionsUrl = (address) => {
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-4 border-hub-200 border-t-hub-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!gig) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Gig not found</p>
        <button onClick={() => navigate(-1)} className="btn-primary mt-4">Go Back</button>
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-600">
        <ArrowLeft className="w-5 h-5" /> Back
      </button>

      <div className="card">
        <div className="card-body space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-500">{gig.client}</p>
              <h1 className="text-xl font-bold">{gig.venue}</h1>
              <p className="text-lg">{gig.location}</p>
            </div>
            <span className={getStatusBadgeClass(gig.status)}>{gig.status}</span>
          </div>

          <div className="grid grid-cols-2 gap-4 pt-4 border-t">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Date</p>
                <p className="font-medium">{formatDateLocal(gig.date, 'EEEE, MMMM d, yyyy')}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Time</p>
                <p className="font-medium">{formatTime(gig.startTime)} - {formatTime(gig.endTime)}</p>
              </div>
            </div>
          </div>

          {/* Address & Directions */}
          {gig.address && (
            <div className="pt-4 border-t">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-gray-500">Address</p>
                  <p className="font-medium">{gig.address}</p>
                  <a 
                    href={getDirectionsUrl(gig.address)} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 mt-2 px-4 py-2 bg-hub-600 text-white rounded-lg hover:bg-hub-700 transition-colors"
                  >
                    <Navigation className="w-4 h-4" />
                    Get Directions
                  </a>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 pt-4 border-t">
            <DollarSign className="w-5 h-5 text-green-600" />
            <div>
              <p className="text-sm text-gray-500">Your Payout</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(gig.payout)}</p>
            </div>
          </div>

          {gig.role && (
            <div className="flex items-center gap-3 pt-4 border-t">
              <FileText className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Role</p>
                <p className="font-medium">{gig.role}</p>
              </div>
            </div>
          )}

          {/* Admin Notes (read-only for contractor) */}
          {gig.notes && (
            <div className="pt-4 border-t">
              <div className="flex items-start gap-2 mb-2">
                <Info className="w-4 h-4 text-hub-600 mt-0.5" />
                <p className="text-sm font-medium text-hub-700">Gig Notes from Admin</p>
              </div>
              <div className="p-3 bg-hub-50 border border-hub-200 rounded-lg">
                <p className="text-gray-700">{gig.notes}</p>
              </div>
            </div>
          )}

          {gig.sets && gig.sets.length > 0 && (
            <div className="pt-4 border-t">
              <p className="text-sm text-gray-500 mb-2">Sets</p>
              <div className="space-y-2">
                {gig.sets.map(set => (
                  <div key={set.setNumber} className="p-3 bg-gray-50 rounded-lg">
                    <p className="font-medium">Set {set.setNumber}</p>
                    <p className="text-sm text-gray-500">
                      {formatTime(set.startTime)} - {formatTime(set.endTime)}
                      {set.notes && ` • ${set.notes}`}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Contractor Notes */}
      <div className="card">
        <div className="card-body">
          <div className="flex items-center gap-2 mb-3">
            <MessageSquare className="w-5 h-5 text-hub-600" />
            <h3 className="font-semibold">Your Notes</h3>
            {notesSaved && (
              <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                Saved!
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 mb-3">
            Add notes about this gig (visible to admin). Report issues, parking info, load-in details, etc.
          </p>
          <div className="flex gap-2">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any notes about this gig..."
              className="input flex-1 min-h-[80px]"
              rows={3}
            />
          </div>
          <button 
            onClick={handleSaveNotes}
            disabled={savingNotes}
            className="btn-primary mt-3 w-full sm:w-auto"
          >
            {savingNotes ? 'Saving...' : 'Save Notes'}
          </button>
        </div>
      </div>

      {/* Actions - only show for pending gigs */}
      {gig.status === 'PENDING' && (
        <div className="flex gap-3">
          <button onClick={handleAccept} className="btn-primary flex-1">Accept Gig</button>
          <button onClick={handleDecline} className="btn-secondary flex-1">Decline</button>
        </div>
      )}
    </div>
  );
}
